import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema, revisionSchema, text } from '../validators'
import { FEED_PROVIDERS, SIGNAL_TRIAGE_STATES, vocab } from '../vocab'

/**
 * Feed signals (D-06, P-FEED-003): candidates for human assessment. They are
 * never auto-published and never set findings. Staff dismiss them with a
 * reason or promote them into a draft incident.
 */

export const SIGNAL_NOVELTY = vocab(['new', 'update', 'repeat'], {
  new: 'New',
  update: 'Update',
  repeat: 'Repeat',
})
export type SignalNovelty = (typeof SIGNAL_NOVELTY.values)[number]

/** Severity bands derived from the generic 0-100 signal score. */
export const SIGNAL_SEVERITY_BANDS = ['low', 'moderate', 'serious', 'severe'] as const
export type SignalSeverity = (typeof SIGNAL_SEVERITY_BANDS)[number]

export function signalSeverityFor(score: number): SignalSeverity {
  if (score >= 85) return 'severe'
  if (score >= 65) return 'serious'
  if (score >= 40) return 'moderate'
  return 'low'
}

/** A held issuer's signal at or above this score raises an alert. */
export const HIGH_SIGNAL_SCORE = 70

export const signalIssuerSchema = z.object({
  id: z.string(),
  legal_name: z.string(),
  country: z.string(),
  sector: z.string(),
  parent_issuer_id: z.string().nullable(),
})

export const signalWorldEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  occurs_on: z.string(),
  event_type: z.string(),
  severity: z.string().nullable(),
  storyline: z.string().nullable(),
})

export const signalSchema = z.object({
  id: z.string(),
  provider_id: z.enum(FEED_PROVIDERS.values),
  provider_name: z.string(),
  attribution: z.string(),
  dataset: z.string(),
  run_id: z.string(),
  provider_record_id: z.string(),
  title: z.string(),
  summary: z.string(),
  event_type: z.string().nullable(),
  severity: z.string().nullable(),
  theme: z.string().nullable(),
  country: z.string().nullable(),
  topics: z.array(z.string()),
  signal_score: z.number().int().nullable(),
  novelty: z.enum(SIGNAL_NOVELTY.values).nullable(),
  /** Distinct dates; null = unverified (GAP-TP-016). */
  event_date: z.string().nullable(),
  event_date_precision: z.string().nullable(),
  publication_date: z.string().nullable(),
  delivered_at: z.string().nullable(),
  retrieved_at: z.string(),
  source_url: z.string().nullable(),
  /** The typed identifier as delivered and the one that matched; never a name (GAP-TP-017). */
  identifier: z.object({ type: z.string(), value: z.string() }).nullable(),
  mapped_by: z.string().nullable(),
  issuer: signalIssuerSchema.nullable(),
  world_event: signalWorldEventSchema.nullable(),
  held_by_org_count: z.number().int(),
  triage_state: z.enum(SIGNAL_TRIAGE_STATES.values),
  triaged_by: z.string().nullable(),
  triaged_by_name: z.string().nullable(),
  triaged_at: z.string().nullable(),
  triage_note: z.string().nullable(),
  promoted_kind: z.string().nullable(),
  promoted_record_id: z.string().nullable(),
  revision: z.number().int(),
  created_at: z.string(),
})
export type Signal = z.infer<typeof signalSchema>

export const signalsQuerySchema = pageQuerySchema.extend({
  provider: z.enum(FEED_PROVIDERS.values).optional(),
  issuer_id: idSchema.optional(),
  severity: z.enum(SIGNAL_SEVERITY_BANDS).optional(),
  status: z.enum(SIGNAL_TRIAGE_STATES.values).optional(),
  /** Free text over title and summary. */
  q: text(0, 120).optional(),
})
export type SignalsQuery = z.infer<typeof signalsQuerySchema>

export const signalDismissSchema = z.strictObject({
  revision: revisionSchema,
  reason: text(8, 2000),
})
export type SignalDismiss = z.infer<typeof signalDismissSchema>

export const signalPromoteSchema = z.strictObject({
  revision: revisionSchema,
  note: text(8, 2000).optional(),
})
export type SignalPromote = z.infer<typeof signalPromoteSchema>

export const signalPromoteResultSchema = z.object({
  signal: signalSchema,
  incident: z.object({ id: z.string(), title: z.string(), issuer_id: z.string() }),
})
export type SignalPromoteResult = z.infer<typeof signalPromoteResultSchema>
